import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Button, Card, ContentSection, Form, Layout, Modal, Paragraph, Spinner } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { intersection } from 'lodash';
import moment from 'moment';
import { MaskedTextFormField, masks } from '../../../../components/FormFields/MaskedTextFormField';
import {
  getPendingContractRegistrationDetails,
  setPendingContractLicensePlates,
  setPendingContractPreparation,
  updatePendingContractRegistrationDetails,
} from '../../../../services/api/miles/miles-services';
import { useRemoveCardTitle } from '../../../../services/common/useRemoveCardTitle';
import { store } from '../../../../services/redux';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { scrollToTop } from '../../../utils';
import { ActivationDatePicker, ActivationLicenseField } from '../../fieldset';
import { getSubPagePaymentValidationSchema } from '../../validation';
import { DocumentsSection } from './DocumentsSection';

export const DashboardSubpagePayment: FunctionComponent = () => {
  const { t } = useTranslation();
  const [modalShown, setModalShown] = useState(false);
  const [licensePlateInvalid, setLicensePlateInvalid] = useState(false);
  const [hasLicensePlate, setHasLicensePlate] = useState<boolean>(false);
  const [updatedDataModalShown, setUpdatedDataModalShown] = useState(false);
  const [requestSentModalShown, setRequestSentModalShown] = useState(false);
  const [formKey, setFormKey] = useState(Math.random());
  const [detailsUpdateLoading, setDetailsUpdateLoading] = useState(false);
  const [initialFormValues, setInitialFromValues] = useState({
    vinNumber: '',
    licensePlate: '',
    deliveryDate: '',
    firstRegistrationDate: '',
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const popupActivationValidationSchema = useMemo(() => getSubPagePaymentValidationSchema(), [t]);
  const [registrationDetailsHandled, setRegistrationDetailsHandled] = useState(false);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const pendingContractId = searchParams.get('contractId');
  const reference = searchParams.get('reference');
  const formRef: any = useRef();
  const handleOpenModal = () => {
    setModalShown(true);
  };

  const handleCloseModal = () => {
    setModalShown(false);
  };

  const handleCloseUpdatedDataModal = () => {
    setUpdatedDataModalShown(false);
  };

  const handleCloseRequestSentModal = (e?) => {
    if (e) {
      e.preventDefault();
    }

    setRequestSentModalShown(false);
    navigate.dashboardAccepted();
  };

  const onCancelHandler = () => {
    navigate.dashboardAccepted();
    scrollToTop();
  };

  const getRegistrationDetails = useCallback(async () => {
    if (pendingContractId) {
      const response = await getPendingContractRegistrationDetails(pendingContractId);
      const hasLicensePlate = !!response?.vehicleData?.licensePlate;
      setHasLicensePlate(hasLicensePlate);
      setInitialFromValues((prev) => ({
        ...prev,
        vinNumber: response.vehicleData.vin,
        firstRegistrationDate: moment(response.vehicleData.initialRegistrationDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        licensePlate: response.vehicleData.licensePlate,
        deliveryDate: moment(response.vehicleData.delivery?.deliveryDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
      }));
    }
  }, [pendingContractId]);

  const handleRegistrationDetailsUpdate = async (form, showModalAfterUpdate: boolean = true) => {
    const { firstRegistrationDate, licensePlate, deliveryDate, vinNumber } = form.values;

    // Validate fields required for Registration data update
    const fieldKeys = ['firstRegistrationDate', 'licensePlate', 'deliveryDate', 'vinNumber'];
    fieldKeys.forEach((key) => {
      form.setFieldTouched(key);
      form.validateField(key);
    });
    const hasErrors = intersection(Object.keys(form.errors), fieldKeys).length > 0;

    if (!hasErrors) {
      setDetailsUpdateLoading(true);
      try {
        await updatePendingContractRegistrationDetails(pendingContractId!, {
          vehicleData: {
            delivery: {
              deliveryDate: moment(deliveryDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            },
            vin: vinNumber,
          },
        });

        if (!hasLicensePlate) {
          await setPendingContractLicensePlates(pendingContractId!, {
            vehicleData: {
              countryCode: 'FR', // Hardcoded for now there's no place I can get this information,
              initialRegistrationDate: moment(firstRegistrationDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
              licensePlate,
            },
          });
        }

        setLicensePlateInvalid(false);
        setDetailsUpdateLoading(false);
        setUpdatedDataModalShown(showModalAfterUpdate);
        setRegistrationDetailsHandled(true);
      } catch (error) {
        setLicensePlateInvalid(true);
        setDetailsUpdateLoading(false);
      }
    }
  };

  useEffect(() => {
    getRegistrationDetails();
  }, [getRegistrationDetails]);

  const handlePaymentRequest = async () => {
    try {
      if (!registrationDetailsHandled) {
        await handleRegistrationDetailsUpdate(formRef.current, false);
      }
      store.dispatch(loadingActions.setLoading(true));
      await setPendingContractPreparation(pendingContractId!);
      store.dispatch(loadingActions.setLoading(false));
      setRequestSentModalShown(true);
      setFormKey(Math.random());
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  useRemoveCardTitle();

  return (
    <>
      <ContentSection pageWrap className="u-pv-none">
        <Formik
          key={formKey}
          enableReinitialize
          initialValues={initialFormValues}
          validationSchema={popupActivationValidationSchema}
          onSubmit={handlePaymentRequest}
          innerRef={formRef}
        >
          {(formProps) => {
            const { handleSubmit, isValid: isFormValid } = formProps;
            const handleSubmitOwn = (e) => {
              handleSubmit(e);
            };
            return (
              <Form onSubmit={handleSubmitOwn}>
                <Layout.Item className="u-text-left u-font-size-fs4 u-pl-none">
                  {t('dashboard:popup:number', { number: reference })}
                </Layout.Item>
                <Layout.Item className="u-text-left u-font-size-fs4 u-pl-none">
                  {t('dashboard:popup:deliverInformationHeaderTitle')}
                </Layout.Item>
                <Card title="">
                  <Layout.Item className="u-mb">
                    <MaskedTextFormField
                      mask={masks.vinMask}
                      fieldName="vinNumber"
                      labelTranslationKey="dashboard:popup:vinNumberTitle"
                      overwrite
                      removeWhitespace
                    />
                  </Layout.Item>
                  <Layout.Item className="u-mb">
                    <ActivationLicenseField
                      name="licensePlate"
                      label={t('dashboard:popup:licensePlateTitle')}
                      required
                      disabled={hasLicensePlate}
                    />
                  </Layout.Item>
                  <Layout.Item className="u-mb">
                    <ActivationDatePicker
                      {...formProps}
                      name="deliveryDate"
                      required
                      label={t('dashboard:popup:deliveryDate')}
                      minDate={moment().subtract(60, 'days').toDate()}
                      maxDate={moment().add(3, 'days').toDate()}
                    />
                  </Layout.Item>
                  <Layout.Item className="u-mb">
                    <ActivationDatePicker
                      {...formProps}
                      required
                      name="firstRegistrationDate"
                      label={t('dashboard:popup:firstRegistrationDateTitle')}
                      maxDate={moment().add(0, 'days').toDate()}
                      disabled={hasLicensePlate}
                    />
                  </Layout.Item>
                  <Layout.Item default="1/3" />
                  <Layout.Item default="1/3" />
                  <Layout.Item className="u-text-right">
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleRegistrationDetailsUpdate(formProps);
                      }}
                    >
                      {detailsUpdateLoading ? <Spinner small /> : t('dashboard:buttons:appliquer')}
                    </Button>
                  </Layout.Item>
                </Card>
                <Layout.Item className="u-text-left u-font-size-fs4 u-pl-none u-mt-xlarge u-mb">
                  {t('dashboard:popup:popupHeaderTitle')}
                </Layout.Item>
                <DocumentsSection
                  contractId={pendingContractId}
                  formProps={formProps}
                  setFieldValue={formProps.setFieldValue}
                />
                <Layout.Item className="u-text-center">
                  <Button
                    className="u-m-small"
                    type="button"
                    onClick={() => {
                      handleOpenModal();
                    }}
                  >
                    {t('dashboard:buttons:cancelButtonTitle')}
                  </Button>
                  <Button className="u-m-small" type="submit" disabled={!isFormValid || licensePlateInvalid}>
                    {t('dashboard:buttons:submitButtonTitle')}
                  </Button>
                  <Modal
                    title={t('app:dashboardActionModalTitle')}
                    hideTitle
                    buttonConfirmText={t('dashboard:buttons:yesButtonTitle')}
                    buttonCancelText={t('dashboard:buttons:noButtonTitle')}
                    id="modalId"
                    onConfirm={onCancelHandler}
                    onCancel={handleCloseModal}
                    onClickOutside={handleCloseModal}
                    onClose={handleCloseModal}
                    shown={modalShown}
                  >
                    <ContentSection pageWrap className="u-pv-small u-text-center">
                      <Paragraph>{t('dashboard:popup:cancelTextTitle')}</Paragraph>
                    </ContentSection>
                  </Modal>
                  <Modal
                    title={t('app:dashboardActionModalTitle')}
                    hideTitle
                    buttonConfirmText={t('dashboard:buttons:nextButtonTitle')}
                    id="updateDataModalId"
                    onConfirm={handleCloseUpdatedDataModal}
                    onClickOutside={handleCloseUpdatedDataModal}
                    onClose={handleCloseUpdatedDataModal}
                    shown={updatedDataModalShown}
                  >
                    <ContentSection pageWrap className="u-pv-small u-text-center">
                      <Paragraph>{t('dashboard:popup:updatedDataTitle')}</Paragraph>
                    </ContentSection>
                  </Modal>
                  <Modal
                    title={t('app:dashboardActionModalTitle')}
                    hideTitle
                    buttonConfirmText={t('dashboard:buttons:closeButtonTitle')}
                    id="requestSentModalId"
                    onConfirm={handleCloseRequestSentModal}
                    onClickOutside={handleCloseRequestSentModal}
                    onClose={() => handleCloseRequestSentModal}
                    shown={requestSentModalShown}
                  >
                    <ContentSection pageWrap className="u-pv-small u-text-center">
                      <Paragraph>{t('dashboard:popup:requestSentTitle')}</Paragraph>
                    </ContentSection>
                  </Modal>
                </Layout.Item>
              </Form>
            );
          }}
        </Formik>
      </ContentSection>
    </>
  );
};
